// The fleet radio's comms engine: a port of the browser radio's (walkie.js) protocol logic, without its page. Channels and
// their SHA-256 ids, the STATE lease (the whole tuning in one message every 5 s), the voice packet, push to talk, and the
// 2.2 private-call state machine, the hardest-won code in the radio:
//   - a call is a SESSION, not a keypress: letting go of the key keeps you on the call, so the reply lands
//   - hanging up suppresses the call hard for a minute (nothing brings it back); an idle timeout or the other side leaving
//     suppresses it softly for 10 s (the lingering channel listing can't re-invite you, but real audio still does)
//   - the first voice packet on a private channel that names you IS the invitation: join on it (fast-join), don't wait for
//     the channel list
//   - a peer who leaves stays left; one call at a time; 60 s of silence ends a call
//   - private audio is AES-GCM encrypted end to end, exactly as the desktop radios do it
// Custody and receipts from day one: every transmission's packets are counted as they go out, and every transmission heard
// is counted as received / decoded / played. Both lines go to the console and to The Chair's server (logs/radio.jsonl).
//
// No DOM here: the audio (microphone, codec, speakers) is behind RadioAudioPort (client/radioaudio.ts), so this runs in
// the unit test (tools/radiounit.ts) as it runs in the page.
import {
  DEPT_RADIO, FLEET_CHANNELS, RADIO_FLAG_ENCRYPTED, RADIO_STATE_MS, SHIP_RADIO, freqCanonical, groupCanonical, b64, chHex, channelId, decodePacket, decryptDirect, deriveDirectKey, directCanonical,
  directMembers, encodePacket, encryptDirect, normUser, unb64, type RadioLink, type RadioLogRec, type WtFrame,
} from "../shared/radio.js";

export interface RadioChannel { canon: string; name: string; label: string; kind: "fleet" | "ship" | "dept" }
// the sound side: the microphone into Opus frames, and Opus frames out to your ear (or, later, a speaker in the room)
export interface RadioAudioPort {
  readonly canTalk: boolean; readonly canHear: boolean; readonly why: string;
  startTx(onOpus: (opus: Uint8Array) => void): Promise<boolean>;     // open the mic and start encoding 40 ms frames
  stopTx(): Promise<void>;                                            // stop; resolves once the encoder's last frames are out
  play(stream: string, opus: Uint8Array, canon: string, rec: { decoded(): void; played(): void }): void;
  endStream(stream: string): void;                                    // a transmission is over: free its decoder
}
export interface RadioDeps {
  sendWt: (m: WtFrame) => void;                     // a frame for the gateway (through The Chair's server)
  log: (rec: RadioLogRec, line: string) => void;    // custody / receipt, for logs/radio.jsonl
  audio: RadioAudioPort | null;
  now?: () => number;
  changed?: () => void;                             // something the panel shows changed
  hint?: (text: string) => void;                    // a short message for the player
  print?: (line: string) => void;                   // the console (custody lines)
  txTailMs?: number;                                // keep sending this long after the key comes up (no clipped last word)
  txSound?: (kind: "start" | "end") => void;        // your own TX start and end sounds (client/radiotx.ts)
}
// the channels on offer: the ship's, the fleet's frequencies, the departments' groups
export function radioChannels(dept: string): RadioChannel[] {
  const out: RadioChannel[] = [{ canon: groupCanonical(SHIP_RADIO.group), name: SHIP_RADIO.name, label: "ship", kind: "ship" }];
  for (const c of FLEET_CHANNELS) out.push({ canon: freqCanonical(c.mhz), name: c.name, label: c.mhz.toFixed(3) + " MHz", kind: "fleet" });
  for (const [id, name] of DEPT_RADIO) out.push({ canon: groupCanonical(id), name, label: id === dept ? "your department" : "department", kind: "dept" });
  return out;
}
// what a player carries by default: the ship's channel (the key talks on it), and listening to Fleet-Net and their department
export function defaultTuning(dept: string): { sel: string; mon: string[] } {
  const ship = groupCanonical(SHIP_RADIO.group), fleet = freqCanonical(FLEET_CHANNELS[0].mhz);
  const mon = [ship, fleet]; if (DEPT_RADIO.some(([id]) => id === dept)) mon.push(groupCanonical(dept));
  return { sel: ship, mon };
}
interface RxStream { key: string; token: number; txId: number; ch: number; canon: string; who: string; received: number; decoded: number; played: number; dropped: number; enc: boolean; last: number; chain: Promise<void> }

export const DIRECT_IDLE_MS = 60000, DIRECT_SUPPRESS_MS = 60000, DIRECT_SOFT_SUPPRESS_MS = 10000;
const RX_IDLE_MS = 1500;          // a transmission heard is over (its receipt written) after this long without a packet
const MAX_DECLARED = 8;           // channels declared at once (the desktop radio has a handful of slots)

export class Radio {
  link: RadioLink = "off"; user = ""; welcomed = false; isAdmin = false;
  voiceToken = 0; epoch = 0;
  channels: RadioChannel[] = []; sel = ""; mon = new Set<string>();
  private ids = new Map<string, number>();                 // canonical -> channel id (hashed once)
  private tuned = new Set<number>();                       // the ids we declared (and so hear)
  people = new Map<string, { talking: boolean }>();
  // the private call (walkie.js 2.2)
  directPeer: string | null = null; directCanon: string | null = null; directChannelId = 0; directConfirmed = false;
  private directKey: CryptoKey | null = null; private directKeyPromise: Promise<CryptoKey | null> | null = null;
  private directLastActivity = 0; private directStartedAt = 0;
  private directSuppressed = new Map<string, { exp: number; hard: boolean }>();   // canonical -> rejoin guard
  private directIdMap = new Map<number, { peer: string; canon: string }>();      // a DIRECT id per operator online -> who (fast-join)
  // transmitting
  txActive = false; txWant = false; private txEnding: ReturnType<typeof setTimeout> | null = null; private txFinishing = false;
  private txId = 0; private seq = 0; private clockStart = 0; private txChain: Promise<void> = Promise.resolve();
  private txCh = 0; private txCanon = ""; private txKeyP: Promise<CryptoKey | null> | null = null; private txPkts = 0; private txAt = 0;
  // receiving
  private streams = new Map<string, RxStream>(); private tokenWho = new Map<number, string>();
  private txStarts: { who: string; at: number; ch: number | null }[] = [];
  untuned = 0;                                             // packets for channels we aren't on (diagnostic)
  lines: string[] = [];                                    // the last custody / receipt lines (the panel shows them)
  private lastStateAt = 0;
  constructor(private d: RadioDeps) {}
  private now() { return this.d.now ? this.d.now() : Date.now(); }
  private changed() { this.d.changed?.(); }
  private hint(t: string) { this.d.hint?.(t); }
  private say(line: string, rec: RadioLogRec) { (this.d.print ?? ((s: string) => console.log("[radio] " + s)))(line); this.lines.unshift(line); this.lines.length = Math.min(this.lines.length, 6); this.d.log(rec, line); this.changed(); }

  // ---------- tuning ----------
  async idOf(canon: string): Promise<number> { let id = this.ids.get(canon); if (id === undefined) { id = await channelId(canon); this.ids.set(canon, id); } return id; }
  nameOf(canon: string): string { return this.channels.find((c) => c.canon === canon)?.name ?? (canon.startsWith("DIRECT:") ? "DM: " + (this.directPeer ?? "") : canon); }
  // the channels on offer, the one the key talks on, the ones you listen to: declared at once, in one message
  async setTuning(channels: RadioChannel[], sel: string, mon: Iterable<string>) {
    this.channels = channels; this.sel = sel; this.mon = new Set(mon);
    await this.retune();
  }
  async select(canon: string) { if (canon === this.sel) return; if (this.txActive || this.txWant) this.endTx(); this.sel = canon; await this.retune(); }
  async monitor(canon: string, on: boolean) { if (on) this.mon.add(canon); else this.mon.delete(canon); await this.retune(); }
  declared(): string[] { const out = [this.sel, ...[...this.mon].filter((c) => c !== this.sel)].filter(Boolean).slice(0, MAX_DECLARED); return out; }
  private async retune() {
    const want = this.declared(); for (const c of want) await this.idOf(c);
    this.tuned = new Set(want.map((c) => this.ids.get(c)!));
    this.changed(); if (this.welcomed) this.sendState();
  }
  // the lease: everything we're tuned to, in ONE message (a re-tune that sent one message per channel briefly un-declared the
  // others mid-call). The private call goes in too: declaring it is what lets the server grant our reply.
  declaration(): WtFrame {
    const radios = this.declared().map((canon) => ({ canonical: canon, name: this.nameOf(canon), toneCode: 0, modulation: "FM" }));
    if (this.directCanon) radios.push({ canonical: this.directCanon, name: "DM: " + (this.directPeer || ""), toneCode: 0, modulation: "FM" });
    return { type: "STATE", radios };
  }
  sendState() { this.lastStateAt = this.now(); this.d.sendWt(this.declaration()); }

  // ---------- the link (The Chair's server tells us) ----------
  onStatus(link: RadioLink, user?: string) {
    this.link = link; if (user) this.user = user;
    if (link !== "on") { this.welcomed = false; if (this.txActive || this.txWant) this.endTx(true); this.endDirect("link", true); this.people.clear(); this.finishStreams(true); }
    this.changed();
  }
  get onAir() { return this.link === "on" && this.welcomed; }

  // ---------- frames from the radio server ----------
  async onWt(m: WtFrame) {
    switch (m.type) {
      case "WELCOME": {
        this.voiceToken = Number(m.voiceToken) >>> 0; this.epoch = Number(m.epoch) | 0; this.welcomed = true; this.isAdmin = !!m.isAdmin;
        this.people.clear();
        for (const c of (m.clients as { username?: string }[] | undefined) ?? []) if (c?.username) this.people.set(c.username, { talking: false });
        if (this.user) this.people.set(this.user, { talking: false });
        await this.rebuildDirectIdMap();
        await this.retune();                                                    // the lease starts: the whole tuning, at once
        await this.scanDirect(m.channels as ChannelInfo[] | undefined);
        this.changed(); break;
      }
      case "CHANNEL_UPDATE": await this.scanDirect(m.channels as ChannelInfo[] | undefined); break;
      case "CLIENT_JOIN": { const u = String(m.username ?? ""); if (u) { this.people.set(u, this.people.get(u) ?? { talking: false }); await this.rebuildDirectIdMap(); this.changed(); } break; }
      case "CLIENT_LEAVE": {
        const u = String(m.username ?? ""); if (!u) break;
        const gone = normUser(u); this.people.delete(u); await this.rebuildDirectIdMap();
        if (this.directPeer && gone === this.directPeer) this.endDirect("peer");
        this.changed(); break;
      }
      case "TX_START": {
        const u = String(m.username ?? ""); this.markTalking(u, true);
        if (u && normUser(u) !== normUser(this.user)) { this.txStarts.push({ who: u, at: this.now(), ch: m.channelId === undefined ? null : Number(m.channelId) >>> 0 }); if (this.txStarts.length > 16) this.txStarts.shift(); }
        break;
      }
      case "TX_END": this.markTalking(String(m.username ?? ""), false); break;
      case "TX_DENY": this.endTx(true); this.hint("TX DENIED: " + String(m.reason || "the radio server refused the transmission")); break;
      case "VOICE_TCP": this.onVoice(String(m.data ?? "")); break;
      case "ERROR": this.hint(String(m.message || "Radio server error")); break;
    }
  }
  private markTalking(name: string, on: boolean) { if (!name) return; const p = this.people.get(name) ?? { talking: false }; p.talking = on; this.people.set(name, p); this.changed(); }
  get rx(): boolean { const me = normUser(this.user); for (const [n, p] of this.people) if (p.talking && normUser(n) !== me) return true; for (const s of this.streams.values()) if (this.now() - s.last < 300) return true; return false; }

  // ---------- the private call (walkie.js 2.2, rule for rule) ----------
  // hard = we hung up on purpose: nothing rejoins for a minute. soft = we idled out or the peer left: for a few seconds ignore
  // the channel LISTING (which lingers and would re-invite us in a loop), but still answer real audio.
  private suppressDirect(canon: string, hard: boolean) { this.directSuppressed.set(canon, { exp: this.now() + (hard ? DIRECT_SUPPRESS_MS : DIRECT_SOFT_SUPPRESS_MS), hard }); }
  isSuppressed(canon: string, hardOnly = false): boolean {
    const s = this.directSuppressed.get(canon); if (!s) return false;
    if (this.now() > s.exp) { this.directSuppressed.delete(canon); return false; }
    return hardOnly ? s.hard : true;
  }
  // the key talks on the private call while one is open, else on the selected channel
  txChanCanon(): string { return this.directCanon && this.directChannelId ? this.directCanon : this.sel; }
  peerDisplay(): string { if (!this.directPeer) return ""; for (const nm of this.people.keys()) if (normUser(nm) === this.directPeer) return nm; return this.directPeer; }
  // auto-join / auto-leave from the server's channel list: the channel's existence IS the invite; both sides confirmed present
  // before a drop counts as the other side hanging up
  async scanDirect(chs: ChannelInfo[] | undefined) {
    if (!chs || !this.user) return;
    const me = normUser(this.user), live = new Set<string>();
    let found: { canon: string; peer: string; subs: number } | null = null;
    for (const c of chs) {
      const canon = c?.canonical || "";
      if (!canon.startsWith("DIRECT:")) continue;
      live.add(canon);
      const members = directMembers(canon);
      if (!members.includes(me) || this.isSuppressed(canon)) continue;
      // 2.2 servers report `declared` (the sides that really joined); older ones only `subscribers` (counts the auto-pinned peer)
      const present = c.declared !== undefined && c.declared !== null ? (c.declared | 0) : (c.subscribers! | 0);
      const peer = members.find((x) => x !== me) || me;
      const peerOnline = [...this.people.keys()].some((nm) => normUser(nm) === peer);
      // a channel with nobody on it is a leftover shell, not an invitation; and someone not connected can't be calling us
      if (canon !== this.directCanon && (present === 0 || (c.subscribers! | 0) === 0 || !peerOnline)) continue;
      if (!found || canon === this.directCanon) found = { canon, peer, subs: present };
    }
    for (const s of [...this.directSuppressed.keys()]) if (!live.has(s)) this.directSuppressed.delete(s);   // the peer let go: future calls allowed
    if (found && this.directCanon !== found.canon) {
      if (this.directCanon) return;                                            // one call at a time
      await this.startDirect(found.peer, found.canon, "auto");
    } else if (found && this.directCanon === found.canon) {
      if ((found.subs | 0) >= 2) this.directConfirmed = true;
      else if (this.directConfirmed) this.endDirect("peer");
    } else if (!found && this.directCanon && (this.directConfirmed || this.now() - this.directStartedAt > 6000)) {
      this.endDirect("peer");                                                  // grace: our own STATE may not be in this list yet
    }
  }
  async startDirect(peer: string, canon: string, how: "auto" | "call") {
    if (this.directCanon === canon) return;
    this.directCanon = canon; this.directPeer = peer; this.directConfirmed = false; this.directKey = null; this.directKeyPromise = null; this.directChannelId = 0;
    this.directStartedAt = this.now();
    const id = await this.idOf(canon);
    if (this.directCanon !== canon) return;                                    // ended while hashing
    this.openDirect(peer, canon, id, how);
  }
  // the call's channel and key (synchronous once the id is known: a fast-join plays the very packet that invited it)
  private openDirect(peer: string, canon: string, id: number, how: "auto" | "call") {
    this.directCanon = canon; this.directPeer = peer; this.directChannelId = id;
    if (!this.directStartedAt || how === "auto") this.directStartedAt = this.now();
    this.directKeyPromise = deriveDirectKey(canon, id)
      .then((k) => { if (this.directCanon === canon) this.directKey = k; return k; })
      .catch((e) => { console.warn("[radio] DM key derivation failed:", e); return null; });
    this.touchDirect(); this.changed(); if (this.welcomed) this.sendState();
    this.hint("Private call: " + this.peerDisplay() + (how === "call" ? " (the radio key talks to them)" : ""));
  }
  endDirect(reason: "idle" | "peer" | "hangup" | "link", quiet = false) {
    if (!this.directCanon) return;
    if (this.txActive || this.txWant) this.endTx(true);
    const canon = this.directCanon;
    if (reason === "idle" || reason === "peer") this.suppressDirect(canon, false);
    this.directCanon = null; this.directPeer = null; this.directChannelId = 0; this.directConfirmed = false; this.directKey = null; this.directKeyPromise = null;
    this.changed(); if (this.welcomed) this.sendState();
    if (!quiet) this.hint(reason === "idle" ? "Private call closed (a minute of silence)" : "Private call ended");
  }
  // only a deliberate hang-up blocks rejoining; idle / peer-left leave the door open for the peer talking again
  hangUp() { if (!this.directCanon) return; this.suppressDirect(this.directCanon, true); this.endDirect("hangup"); }
  // a name picked: a private call with them (picked again: hang up)
  async callPeer(name: string) {
    if (!this.user || normUser(name) === normUser(this.user)) return;
    const canon = directCanonical(this.user, name);
    if (this.directCanon === canon) { this.hangUp(); return; }
    if (this.directCanon) this.hangUp();
    this.directSuppressed.delete(canon);                                       // calling them yourself lifts your own guard
    await this.startDirect(normUser(name), canon, "call");
  }
  private touchDirect() { this.directLastActivity = this.now(); }
  // one DIRECT id per operator online, for fast-join
  async rebuildDirectIdMap() {
    this.directIdMap.clear(); if (!this.user) return;
    const me = normUser(this.user);
    await Promise.all([...this.people.keys()].map(async (name) => {
      const peer = normUser(name); if (!peer || peer === me) return;
      const canon = directCanonical(me, peer); this.directIdMap.set(await this.idOf(canon), { peer, canon });
    }));
  }

  // ---------- the clock: the lease, the call's idle timer, finished receptions ----------
  tick() {
    const now = this.now();
    if (this.welcomed && now - this.lastStateAt >= RADIO_STATE_MS) this.sendState();
    if (this.directCanon) { if (this.txActive) this.touchDirect(); else if (now - this.directLastActivity >= DIRECT_IDLE_MS) this.endDirect("idle"); }
    this.finishStreams(false);
    while (this.txStarts.length && now - this.txStarts[0].at > 10000) this.txStarts.shift();
  }

  // ---------- transmitting ----------
  get keyed() { return this.txWant || this.txActive; }
  async beginTx(): Promise<boolean> {
    if (this.txEnding && this.txActive && !this.txFinishing) { clearTimeout(this.txEnding); this.txEnding = null; return true; }   // keyed again inside the tail: carry on
    if (this.txActive || this.txWant || !this.onAir) return false;
    const a = this.d.audio;
    if (!a || !a.canTalk) { this.hint(a?.why || "This browser can't send radio voice."); return false; }
    this.txWant = true; this.changed();
    const ok = await a.startTx((opus) => this.onEncoded(opus));
    // the channel first, then on the air with nothing to wait for between: a key let go while the channel's id was looked up used to
    // end a transmission that hadn't started, then the start went out after the end and the server kept us keyed (Grey, 2026-10-23)
    const canon = this.txChanCanon(), ch = ok ? await this.idOf(canon) : 0;
    if (!ok || !this.txWant || !this.onAir) { if (ok) await a.stopTx(); this.txWant = false; this.changed(); return false; }
    this.txActive = true; this.txId = (this.txId + 1) & 0xffff; if (this.txId === 0) this.txId = 1;
    this.seq = 0; this.clockStart = this.now(); this.txAt = this.clockStart; this.txPkts = 0;
    this.txCanon = canon; this.txCh = ch;
    this.txKeyP = this.directCanon && this.txCanon === this.directCanon ? this.directKeyPromise : null;   // a private call: encrypted (waits for the key rather than send the first frames in the clear)
    if (this.directPeer) this.touchDirect();
    this.d.sendWt({ type: "PTT_DOWN", channelId: this.txCh });
    try { this.d.txSound?.("start"); } catch { /* no audio */ }
    this.changed(); return true;
  }
  endTx(now = false) {
    if (this.txWant && !this.txActive) { this.txWant = false; this.changed(); return; }    // let go before the mic opened
    if (!this.txActive || this.txFinishing) return;
    if (this.txEnding) { if (!now) return; clearTimeout(this.txEnding); this.txEnding = null; }
    const tail = now ? 0 : this.d.txTailMs ?? 0;
    if (tail > 0) this.txEnding = setTimeout(() => { this.txEnding = null; void this.finishTx(); }, tail);
    else void this.finishTx();
  }
  private async finishTx() {
    this.txFinishing = true;
    try { await this.d.audio?.stopTx(); } catch { /* closed */ }              // the encoder's last frames go out first
    this.txActive = false; this.txWant = false;
    if (this.directPeer) this.touchDirect();
    const ch = this.txCh, canon = this.txCanon, id = this.txId, enc = !!this.txKeyP;
    await this.txChain;
    this.d.sendWt({ type: "PTT_UP", channelId: ch });
    try { this.d.txSound?.("end"); } catch { /* no audio */ }
    this.txFinishing = false;
    this.say(`TX end ${this.user} tx=${id} ch=${chHex(ch)} pkts=${this.txPkts}`, { kind: "tx", tx: id, ch: chHex(ch), canon, pkts: this.txPkts, ms: Math.round(this.now() - this.txAt), enc });
  }
  // one 40 ms Opus frame from the encoder: into a packet, encrypted on a private call, out. Built on one promise chain so the
  // (async) encryption never reorders frames.
  onEncoded(opus: Uint8Array) {
    if (!this.txActive) return;
    const chan = this.txCh, mySeq = this.seq, txId = this.txId, token = this.voiceToken, epoch = this.epoch, keyP = this.txKeyP;
    const ts = Math.max(0, Math.round(this.now() - this.clockStart)) >>> 0;
    this.seq = (this.seq + 1) & 0xffff;
    this.txChain = this.txChain.then(async () => {
      const key = keyP ? await keyP : null;
      const payload = key ? await encryptDirect(key, opus, chan, mySeq) : opus;
      const pkt = encodePacket({ token, epoch, channel: chan, txId, seq: mySeq, timestamp: ts, flags: key ? RADIO_FLAG_ENCRYPTED : 0, payload });
      this.d.sendWt({ type: "VOICE_TCP", data: b64(pkt) }); this.txPkts++;
    }).catch(() => {});
  }

  // ---------- receiving ----------
  onVoice(data: string) {
    if (!data) return;
    let raw: Uint8Array; try { raw = unb64(data); } catch { return; }
    const p = decodePacket(raw); if (!p) return;
    if (p.token === this.voiceToken) return;                                   // our own echo
    let isDirect = !!this.directChannelId && p.channel === this.directChannelId;
    if (!isDirect && !this.tuned.has(p.channel)) {
      // 2.2 fast-join: a packet on a DIRECT id that names us IS a private call (the server relays DM audio to its two members
      // only): join now, and play this very packet, rather than wait for the channel list
      const dm = this.directIdMap.get(p.channel);
      if (dm && !this.directCanon && !this.isSuppressed(dm.canon, true)) { this.openDirect(dm.peer, dm.canon, p.channel, "auto"); isDirect = true; }
      else { this.untuned++; return; }
    }
    const encrypted = (p.flags & RADIO_FLAG_ENCRYPTED) === 1;
    const key = `${p.token}:${p.txId}`, now = this.now();
    let s = this.streams.get(key);
    if (!s) {
      const canon = isDirect ? this.directCanon! : (this.declared().find((c) => this.ids.get(c) === p.channel) ?? "?");
      s = { key, token: p.token, txId: p.txId, ch: p.channel, canon, who: this.whoFor(p.token, p.channel), received: 0, decoded: 0, played: 0, dropped: 0, enc: encrypted, last: now, chain: Promise.resolve() };
      this.streams.set(key, s);
    }
    s.last = now; s.received++;
    if (encrypted && !isDirect) { s.dropped++; return; }                      // encrypted fleet traffic: not supported (no key)
    if (isDirect) this.touchDirect();
    if (!p.payload.length) { s.dropped++; return; }
    const keyP = encrypted ? (this.directKey ? Promise.resolve(this.directKey) : this.directKeyPromise) : null, st = s, canon = s.canon;
    st.chain = st.chain.then(async () => {
      const opus = encrypted ? await decryptDirect(keyP ? await keyP : null, p.payload) : p.payload;
      if (!opus || !opus.length) { st.dropped++; return; }
      const a = this.d.audio; if (!a || !a.canHear) return;
      a.play(st.key, opus, canon, { decoded: () => { st.decoded++; }, played: () => { st.played++; } });
    }).catch(() => { st.dropped++; });
    this.changed();
  }
  // who a transmission is from: the packet only carries the sender's token, so it's matched to the latest TX_START
  private whoFor(token: number, ch: number): string {
    const known = this.tokenWho.get(token); if (known) return known;
    const now = this.now(), busy = new Set([...this.streams.values()].map((s) => s.who));
    const cands = this.txStarts.filter((t) => now - t.at < 3000 && !busy.has(t.who));
    const hit = [...cands].reverse().find((t) => t.ch === ch) ?? [...cands].reverse().find((t) => t.ch === null) ?? cands[cands.length - 1];
    if (hit) { this.tokenWho.set(token, hit.who); return hit.who; }
    return "#" + chHex(token);
  }
  // a transmission heard is over: its receipt (sent to the log; pairs with the sender's TX end)
  private finishStreams(all: boolean) {
    const now = this.now();
    for (const s of [...this.streams.values()]) {
      if (!all && now - s.last < RX_IDLE_MS) continue;
      this.streams.delete(s.key); this.d.audio?.endStream(s.key);
      const extra = (s.dropped ? ` dropped=${s.dropped}` : "") + (this.d.audio?.canHear ? "" : " (no voice codec in this browser)");
      this.say(`RX receipt: ${this.user} heard ${s.who} tx=${s.txId}: received=${s.received} played=${s.played}${extra}`, { kind: "rx", who: s.who, tx: s.txId, ch: chHex(s.ch), canon: s.canon, received: s.received, decoded: s.decoded, played: s.played, dropped: s.dropped, enc: s.enc });
    }
  }
  // the receptions in flight finish their async work (tests, and a clean receipt)
  async settle() { await Promise.all([...this.streams.values()].map((s) => s.chain)); await this.txChain; }
}
interface ChannelInfo { canonical?: string; declared?: number | null; subscribers?: number }
